import type { ElementType } from 'react'
import { Link } from 'react-router-dom'
import { Check, Bell, Plus, Clock, Mail, Star, X, CalendarClock, MailPlus } from 'lucide-react'
import type { AppNotification } from '@fellowship/shared'
import { timeAgo } from './format'

export const NOTIF_ICON_CFG: Record<string, { bg: string; color: string; Icon: ElementType }> = {
  // Types currently generated by the API
  new_event:          { bg: 'var(--primary-soft)', color: 'var(--primary)', Icon: Plus },
  event_invite:       { bg: '#fce7f3', color: '#db2777', Icon: MailPlus },
  event_cancelled:    { bg: '#fee2e2', color: '#dc2626', Icon: X },
  event_time_changed: { bg: '#fef3c7', color: '#d97706', Icon: CalendarClock },
  // Types reserved for upcoming features (RSVP approvals, reminders, follows)
  rsvp_approved:      { bg: '#dcfce7', color: '#16a34a', Icon: Check },
  rsvp_declined:      { bg: '#fee2e2', color: '#dc2626', Icon: X },
  reminder:           { bg: '#fef3c7', color: '#d97706', Icon: Clock },
  message:            { bg: '#f1f5f9', color: '#64748b', Icon: Mail },
  follow:             { bg: '#fce7f3', color: '#db2777', Icon: Star },
}

function NotifIcon({ type, size = 36 }: { type: string; size?: number }) {
  const cfg = NOTIF_ICON_CFG[type] ?? { bg: 'var(--primary-soft)', color: 'var(--primary)', Icon: Bell }
  const { Icon, bg, color } = cfg
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={size * 0.42} strokeWidth={2.2} style={{ color }} />
    </div>
  )
}

interface NotificationRowProps {
  n: AppNotification
  /** Compact variant for the Overview rail (smaller icon, tighter padding). */
  compact?: boolean
  /** Called when the row is activated (clicked) — used to mark it read. */
  onRead?: (id: string) => void
}

export function NotificationRow({ n, compact, onRead }: NotificationRowProps) {
  const unread = !n.readAt
  const handleActivate = () => {
    if (unread) onRead?.(n.id)
  }
  const row = (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: compact ? '10px 12px' : '13px 14px',
      borderRadius: 12,
      background: unread ? 'var(--primary-soft)' : 'var(--surface)',
      border: '1px solid var(--line-soft)',
      cursor: n.url ? 'pointer' : 'default',
    }}>
      <div style={{
        width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 9,
        background: unread ? 'var(--primary)' : 'transparent',
      }} />
      <NotifIcon type={n.type} size={compact ? 30 : 36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: compact ? 12.5 : 13.5, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.45 }}>
          {n.title}
        </div>
        {!compact && (
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2, lineHeight: 1.4 }}>{n.body}</div>
        )}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', marginTop: 2, flexShrink: 0 }}>
        {timeAgo(n.createdAt)}
      </div>
    </div>
  )

  if (n.url) {
    return (
      <Link
        to={n.url}
        onClick={handleActivate}
        style={{ textDecoration: 'none', display: 'block' }}
        aria-label={n.title}
      >
        {row}
      </Link>
    )
  }
  // No destination to navigate to, but clicking an unread item still marks it read.
  if (unread && onRead) {
    return (
      <button
        type="button"
        onClick={handleActivate}
        style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        aria-label={n.title}
      >
        {row}
      </button>
    )
  }
  return row
}

export type NotificationFilterKey = 'all' | 'unread' | 'rsvps' | 'new_events' | 'reminders'

export const FILTER_MATCH: Record<NotificationFilterKey, (n: AppNotification) => boolean> = {
  all:        () => true,
  unread:     (n) => !n.readAt,
  rsvps:      (n) => n.type.startsWith('rsvp'),
  new_events: (n) => n.type === 'new_event',
  reminders:  (n) => n.type === 'reminder',
}
